import * as React from 'react';
import {connect} from 'react-redux';
import {IconButton, WithStyles, withStyles} from '@material-ui/core';
import Button from '@components/common/button/Button';
import SendRoundedIcon from '@material-ui/icons/SendRounded';
import TranslateNew from '@translate/TranslateNew';
import * as EventActions from '@services/events/event.action';
import BackArrowIcon from "@material-ui/icons/ArrowBackIosRounded";
import { EditPaneStyle } from '../../EditPane.style';
import { ISchedule } from '@app-types/websocket/schedule.types';
import Autosize from '@components/chat/Autosize';
import { IAiScheduleMessage, IAiScheduleTurn, IAiScheduleUpdate } from '@app-types/websocket/aiSchedule.types';
import { primaryColor, secondaryColor, styleColors } from 'src/main.style';

const tn: TranslateNew = TranslateNew.getInstance();
const THINKING_MESSAGE_INTERVAL_MS:number = 3400;
// Matches the hard cap on the shared chat textarea (Autosize maxLength).
const MAX_PROMPT_CHARS:number = 5000;

interface IConversationMessage {
  id:number|string;
  role:"assistant"|"user";
  content:string;
}

interface IState {
  draft:string;
  originalSchedule:ISchedule|undefined;
  messages:IConversationMessage[];
  awaitingAiResponse:boolean;
  sessionId:number|undefined;
  thinkingMessageIndex:number;
}

interface IProps extends WithStyles<typeof EditPaneStyle> {
  close:(finished:boolean) => void;
  scheduleEditBase:ISchedule|undefined;
  scheduleEditPreview:ISchedule|undefined;
  updatePreview:(schedule:ISchedule) => void;
  updateBase:(schedule:ISchedule) => void;
  setHeight: (pixels:number) => void;
  aiScheduleTurn: (turn:IAiScheduleTurn) => void;
  clearAiScheduleUpdate: () => void;
  aiScheduleUpdate:IAiScheduleUpdate|undefined;
  isOnline:boolean;
  onPromptSent:() => void;
  onScheduleApplied:(schedule:ISchedule) => void;
}

class ScheduleAiEdit extends React.Component<IProps, IState> {
  private threadRef:React.RefObject<HTMLDivElement> = React.createRef();
  private appliedAssistantKeys:Set<string> = new Set();
  private localMessageId:number = 0;
  private thinkingMessageTimer:number|undefined;

  constructor(props: IProps) {
    super(props);
    this.state = {
      draft:"",
      originalSchedule:undefined,
      messages:[this.getIntroMessage()],
      awaitingAiResponse:false,
      sessionId:undefined,
      thinkingMessageIndex:0,
    };
  }

  public componentDidMount() {
    this.setState({originalSchedule:this.props.scheduleEditBase});
    this.props.clearAiScheduleUpdate();
    this.props.setHeight(this.getPaneHeight());
    this.scrollThreadToBottom();
  }

  public componentWillUnmount(): void {
    this.stopThinkingMessageLoop();
    this.props.clearAiScheduleUpdate();
  }

  public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>): void {
    if (prevProps.aiScheduleUpdate !== this.props.aiScheduleUpdate) {
      this.handleAiScheduleUpdate();
    }

    if (prevState.messages.length !== this.state.messages.length
      || prevState.awaitingAiResponse !== this.state.awaitingAiResponse) {
      this.scrollThreadToBottom();
    }
  }

  public render() {
    const {classes} = this.props;
    const {draft, messages} = this.state;
    const isWorking = this.isWaitingForAssistant();
    const disableSend = !this.props.isOnline || this.props.scheduleEditBase === undefined
      || draft.trim() === "" || draft.trim().length > MAX_PROMPT_CHARS || isWorking;
    const hasPendingChanges = this.hasPendingChanges();

    return (<>
      <div className={classes.paneOptions} style={{flexShrink: 0}}>
        <Button className={classes.paneOption} startIcon={<BackArrowIcon />} classes={{text:classes.backButton}} 
          style={{textAlign: 'start'}} onClick={() => {
            this.cancel();
          }}>
          {tn.lang.cancel}
        </Button>
        <div className={classes.paneOption} style={{minHeight:'20px', fontSize:'12px'}}>{""}</div>
        <div className={classes.paneOption} style={{minHeight:'20px', fontSize:'12px'}}>{""}</div>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: styleColors.white,
      }}>
        <div
          ref={this.threadRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
          {messages.map((message) => this.renderMessage(message))}
          {isWorking && this.renderTypingMessage()}
        </div>

        {hasPendingChanges && (
          <div style={{
            padding: '12px 18px',
            backgroundColor: styleColors.white,
            borderTop: `1px solid ${styleColors.greyLight3}`,
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{fontSize: '12px', color: styleColors.greyDark1}}>
              {tn.lang.scheduleAiEditPreviewUpdated}
            </div>
            <div style={{display: 'flex', gap: '10px', flexShrink: 0}}>
              <Button
                variant='contained'
                color='primary'
                classes={{contained:classes.buttonContained}}
                onClick={() => this.finish()}
                disabled={isWorking}>
                {tn.lang.show}
              </Button>
            </div>
          </div>
        )}


        <div className={classes.chatBottomBar} style={{flexShrink: 0}}>
          <div className={classes.chatInputBox}>
            <Autosize value={draft} 
              placeholder={tn.lang.scheduleAiEditPlaceholder}
              handleKeyPress={(evt) => this.handleComposerKeyPress(evt)}
              onChange={(evt) => this.setState({draft: evt.target.value})}/>
          </div>
          <IconButton style={{backgroundColor:(disableSend?"#CCCCCC":primaryColor)}} 
            disabled={disableSend} onClick={() => this.handleSendMessage()}>
            <SendRoundedIcon style={{fill: "#FFFFFF", fontSize:'16px'}} />
          </IconButton>
        </div>
      </div>
      </>
    );
  }

  private renderMessage(message:IConversationMessage):JSX.Element {
    const isUser = message.role === "user";
    const speakerLabel = isUser ? tn.lang.scheduleAiEditUserLabel : tn.lang.scheduleAiEditAssistantLabel;

    return (
      <div key={message.id} style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        gap: '6px',
      }}>
        <div style={{
          fontSize: '11px',
          color: styleColors.greyMedium3,
          padding: '0px 6px',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}>
          {speakerLabel}
        </div>
        <div style={{
          maxWidth: '88%',
          padding: '12px 14px',
          borderRadius: isUser ? '20px 20px 6px 20px' : '20px 20px 20px 6px',
          backgroundColor: isUser ? secondaryColor : styleColors.greyLight2,
          color: isUser ? '#ffffff' : styleColors.greyDark2,
          border: isUser ? '1px solid '+secondaryColor : `1px solid ${styleColors.greyMedium1}`,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          boxShadow: isUser
            ? '0px 10px 20px rgba(93, 57, 95, 0.18)'
            : '0px 6px 14px rgba(0, 0, 0, 0.05)',
        }}>
          {message.content}
        </div>
      </div>
    );
  }

  private renderTypingMessage():JSX.Element {
    const thinkingMessages = this.getThinkingMessages();
    const thinkingMessage = thinkingMessages[this.state.thinkingMessageIndex] || thinkingMessages[0];

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '4px',
        padding: '2px 6px 0px 6px',
      }}>
        <div style={{
          fontSize: '11px',
          color: styleColors.greyMedium3,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}>
          {tn.lang.scheduleAiEditThinkingLabel}
        </div>
        <div style={{
          maxWidth: '88%',
          color: styleColors.greyDark1,
          lineHeight: 1.5,
          fontSize: '14px',
        }}>
          {thinkingMessage}
        </div>
      </div>
    );
  }

  private handleComposerKeyPress(evt:React.KeyboardEvent<HTMLTextAreaElement>) {
    if (evt.key === "Enter" && !evt.shiftKey) {
      evt.preventDefault();
      this.handleSendMessage();
    }
  }

  private handleSendMessage() {
    const prompt = this.state.draft.trim();
    const scheduleEditBase = this.props.scheduleEditBase;
    if (prompt.length === 0 || prompt.length > MAX_PROMPT_CHARS || scheduleEditBase === undefined || this.isWaitingForAssistant()) {
      return;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const optimisticMessages = [...this.state.messages, this.createLocalMessage("user", prompt)];

    this.setState({
      messages:optimisticMessages,
      draft:"",
      awaitingAiResponse:true,
      thinkingMessageIndex:0,
    });
    this.startThinkingMessageLoop();
    this.props.onPromptSent();
    this.props.aiScheduleTurn({
      sessionId:this.state.sessionId,
      text:prompt,
      currentSchedule:scheduleEditBase,
      fileIds:[],
      timezone,
    });
  }

  private handleAiScheduleUpdate():void {
    const update = this.props.aiScheduleUpdate;
    if (!this.isRelevantUpdate(update)) {
      return;
    }

    const nextMessages = this.mergeMessagesFromUpdate(update);
    const nextSessionId = update.sessionId;
    const isInProgress = update.status === "inProgress";

    this.setState({
      sessionId:nextSessionId,
      messages:nextMessages,
      awaitingAiResponse:isInProgress,
    });

    if (isInProgress) {
      this.startThinkingMessageLoop();
    } else {
      this.stopThinkingMessageLoop();
    }

    if (update.status === "completed" && update.updatedSchedule !== undefined) {
      const latestAssistant = this.getLatestAssistantMessage(update.messages);
      if (latestAssistant !== undefined) {
        const appliedKey = nextSessionId+":"+this.getConversationMessageId(latestAssistant);
        if (!this.appliedAssistantKeys.has(appliedKey)) {
          this.appliedAssistantKeys.add(appliedKey);
          this.applyUpdatedSchedule(update.updatedSchedule);
        }
      }
    }
  }

  private mergeMessagesFromUpdate(update:IAiScheduleUpdate):IConversationMessage[] {
    const messages:IConversationMessage[] = this.state.messages
      .filter((message) => message.id !== "intro")
      .map((message) => ({...message}));

    for (const message of this.normalizeMessagesFromUpdate(update)) {
      const existingIndex = this.findMatchingMessageIndex(messages, message);
      if (existingIndex >= 0) {
        messages[existingIndex] = message;
      } else {
        messages.push(message);
      }
    }

    if (update.status === "failed" && update.errorMessage) {
      const failedMessage:IConversationMessage = {
        id:"failed-"+(update.sessionId !== undefined ? update.sessionId : "unknown"),
        role:"assistant",
        content:update.errorMessage,
      };
      const existingIndex = this.findMatchingMessageIndex(messages, failedMessage);
      if (existingIndex >= 0) {
        messages[existingIndex] = failedMessage;
      } else {
        messages.push(failedMessage);
      }
    }

    return [this.getIntroMessage(), ...messages];
  }

  private normalizeMessagesFromUpdate(update:IAiScheduleUpdate):IConversationMessage[] {
    const messages:IConversationMessage[] = [];

    for (const message of (update.messages ? update.messages : [])) {
      if (message.role === "user") {
        const text = message.text ? message.text.trim() : "";
        if (text.length > 0) {
          messages.push({
            id:this.getConversationMessageId(message),
            role:"user",
            content:text,
          });
        }
        continue;
      }

      if (message.role === "assistant") {
        if (message.status === "inProgress") {
          continue;
        }

        const assistantText = this.getAssistantText(message, update);
        if (assistantText.length > 0) {
          messages.push({
            id:this.getConversationMessageId(message),
            role:"assistant",
            content:assistantText,
          });
        }
      }
    }

    return messages;
  }

  private findMatchingMessageIndex(
    messages:IConversationMessage[],
    nextMessage:IConversationMessage
  ):number {
    if (nextMessage.id !== undefined) {
      const byId = messages.findIndex((message) => message.id === nextMessage.id);
      if (byId >= 0) {
        return byId;
      }
    }

    return messages.findIndex((message) =>
      typeof message.id === "string"
      && message.id.indexOf("local-") === 0
      && message.role === nextMessage.role
      && message.content === nextMessage.content
    );
  }

  private getConversationMessageId(message:IAiScheduleMessage):number|string {
    if (message.id !== undefined) {
      return message.id;
    }
    if (message.createdAt !== undefined) {
      return (message.role ? message.role : "unknown")+"-"+message.createdAt;
    }
    return this.nextLocalId();
  }

  private getAssistantText(message:IAiScheduleMessage, update:IAiScheduleUpdate):string {
    const text = message.text ? message.text.trim() : "";
    if (text.length > 0) {
      return text;
    }
    if (message.status === "failed" && update.errorMessage) {
      return update.errorMessage;
    }
    return "";
  }

  private getLatestAssistantMessage(messages:IAiScheduleMessage[]|undefined):IAiScheduleMessage|undefined {
    if (messages === undefined || messages.length === 0) {
      return undefined;
    }

    let latestAssistant:IAiScheduleMessage|undefined;
    for (const message of messages) {
      if (message.role === "assistant") {
        latestAssistant = message;
      }
    }
    return latestAssistant;
  }

  private applyUpdatedSchedule(updatedSchedule:ISchedule):void {
    const scheduleCopy:ISchedule = JSON.parse(JSON.stringify(updatedSchedule));
    this.props.updatePreview(scheduleCopy);
    this.props.updateBase(scheduleCopy);
    this.props.onScheduleApplied(scheduleCopy);
    setTimeout(() => {this.props.setHeight(this.getPaneHeight());}, 200);
  }

  private isRelevantUpdate(update:IAiScheduleUpdate|undefined): update is IAiScheduleUpdate {
    if (update === undefined || update.sessionId === undefined) {
      return false;
    }

    if (this.state.sessionId !== undefined) {
      return update.sessionId === this.state.sessionId;
    }

    return this.state.awaitingAiResponse;
  }

  private isWaitingForAssistant():boolean {
    const update = this.props.aiScheduleUpdate;
    if (this.state.awaitingAiResponse) {
      return true;
    }
    return this.isRelevantUpdate(update) && update.status === "inProgress";
  }

  private cancel():void {
    this.stopThinkingMessageLoop();
    if (this.state.originalSchedule !== undefined) {
      this.props.updateBase(this.state.originalSchedule);
      this.props.updatePreview(this.state.originalSchedule);
    }
    this.props.clearAiScheduleUpdate();
    this.props.close(false);
  }

  private finish():void {
    this.stopThinkingMessageLoop();
    this.props.clearAiScheduleUpdate();
    this.props.close(true);
  }

  private hasPendingChanges():boolean {
    return this.state.originalSchedule !== undefined
      && this.props.scheduleEditBase !== undefined
      && this.props.scheduleEditBase !== this.state.originalSchedule;
  }

  private createLocalMessage(role:"assistant"|"user", content:string):IConversationMessage {
    return {
      id:"local-"+this.nextLocalId(),
      role,
      content,
    };
  }

  private getIntroMessage():IConversationMessage {
    return {
      id:"intro",
      role:"assistant",
      content:tn.lang.scheduleAiEditIntro,
    };
  }

  private nextLocalId():number {
    this.localMessageId += 1;
    return this.localMessageId;
  }

  private scrollThreadToBottom():void {
    if (this.threadRef.current) {
      window.requestAnimationFrame(() => {
        if (this.threadRef.current) {
          this.threadRef.current.scrollTop = this.threadRef.current.scrollHeight;
        }
      });
    }
  }

  private getThinkingMessages():string[] {
    if (tn.lang.scheduleAiEditThinkingMessages.length > 0) {
      return tn.lang.scheduleAiEditThinkingMessages;
    }

    return [""];
  }

  private startThinkingMessageLoop():void {
    if (this.thinkingMessageTimer !== undefined) {
      return;
    }

    this.thinkingMessageTimer = window.setInterval(() => {
      const thinkingMessages = this.getThinkingMessages();
      this.setState((prevState) => ({
        thinkingMessageIndex:(prevState.thinkingMessageIndex + 1) % thinkingMessages.length,
      }));
    }, THINKING_MESSAGE_INTERVAL_MS);
  }

  private stopThinkingMessageLoop():void {
    if (this.thinkingMessageTimer === undefined) {
      return;
    }

    window.clearInterval(this.thinkingMessageTimer);
    this.thinkingMessageTimer = undefined;
  }

  private getPaneHeight():number {
    return Math.max(320, Math.min(window.innerHeight - 115, 560));
  }
}

const mapDispatchToProps = {
  aiScheduleTurn: EventActions.aiScheduleTurn,
  clearAiScheduleUpdate: EventActions.clearAiScheduleUpdate,
};

function mapStateToProps(state: any) {
  return {
    aiScheduleUpdate: state.event.aiScheduleUpdate,
    isOnline:state.connection.loadCompleted,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(EditPaneStyle)(ScheduleAiEdit));



// WEBPACK FOOTER //
// ./src/components/calendar2/editPane/editActions/AiEdit/ScheduleAiEdit.tsx